/**
 * Synthetic sample data for the E1 screens.
 *
 * No customer material is in this repository (OP-9, .gitignore). Everything
 * here is invented — the geometry deliberately reproduces two situations the
 * project argues about rather than any real drawing:
 *
 *   Run A  a fully dimensioned closed run: Σh = 0, Σv = 0.
 *   Run B  one section read as 17.500 mm instead of 1.500 mm. The run shoots
 *          off the page and the gap is 16.000 mm — geometry as the error
 *          detector (E-13).
 *   Run C  a run with an undimensioned section: no closure verdict (E-14/E-15).
 */

import type { CasePlan, CaseSummary, ExtractionRun } from "./types";

export const SAMPLE_CASE: CaseSummary = {
  id: "case_demo_0001",
  reference: "V-2026-0042",
  title: "Musterhaus Beispielweg — Rostlauf Süd/West",
  createdAt: "2026-08-03T09:12:00+02:00",
  documents: [
    {
      id: "doc_demo_0001",
      filename: "handskizze-musterhaus.jpg",
      mimeType: "image/jpeg",
      pageCount: 1,
      sizeBytes: 2_418_332,
    },
  ],
  classification: {
    result: "valid",
    reason:
      "Handskizze mit bemaßten Rostabschnitten. Rosttyp im Text genannt, Maße den Abschnitten zuordenbar.",
    confidence: 0.94,
  },
};

/** Second intake outcome: the document is outside the trade model (S-1). */
export const SAMPLE_CASE_SPECIAL: CaseSummary = {
  id: "case_demo_0002",
  reference: "V-2026-0043",
  title: "Innenraum-Bodengitter, zwei Einzelöffnungen",
  createdAt: "2026-08-03T10:04:00+02:00",
  documents: [
    {
      id: "doc_demo_0002",
      filename: "bodengitter-oeffnungen.pdf",
      mimeType: "application/pdf",
      pageCount: 2,
      sizeBytes: 984_112,
    },
  ],
  classification: {
    result: "special_case",
    reason:
      "Kein Rostlauf entlang einer Fassade, sondern einzelne Bodenöffnungen im Innenraum — teilweise nicht rechtwinklig. Das Fachmodell greift nicht; Bearbeitung im Dialog.",
    confidence: 0.94,
  },
};

/** Third intake outcome: rejected. A rejection never produces values (UI-Arch §6). */
export const SAMPLE_CASE_REJECTED: CaseSummary = {
  id: "case_demo_0003",
  reference: "V-2026-0044",
  title: "Architekten-Werkplan Erdgeschoss",
  createdAt: "2026-08-03T10:41:00+02:00",
  documents: [
    {
      id: "doc_demo_0003",
      filename: "werkplan-eg.pdf",
      mimeType: "application/pdf",
      pageCount: 4,
      sizeBytes: 3_711_902,
    },
  ],
  classification: {
    result: "rejected",
    reason:
      "Bemaßt ist das Gebäude, nicht der Rostlauf. Aus diesem Plan lassen sich keine Rostabschnitte ableiten — bitte bemaßte Skizze der Rostabschnitte anfordern.",
    confidence: 0.94,
  },
};

export const SAMPLE_EXTRACTION: ExtractionRun = {
  caseId: SAMPLE_CASE.id,
  promptId: "auslese-v0.1",
  model: "mistral-ocr-4-0 + mistral-medium-2604",
  extractedAt: "2026-08-03T09:13:41+02:00",
  values: [
    {
      id: "val_001",
      field: "run_a.grating_type",
      label: "Rosttyp",
      valueMm: null,
      valueText: "Pressrost",
      confidence: "high",
      confidenceLabel: "hoch",
      confidenceStep: 3,
      confidenceStepsTotal: 3,
      status: "confirmed",
      provenance: "confirmed",
      source: {
        documentId: "doc_demo_0001",
        page: 1,
        bbox: { x: 0.11, y: 0.07, width: 0.22, height: 0.04 },
        originalText: "Pressrost 30/30",
      },
      confirmedBy: "",
      confirmedAt: "2026-08-03T09:20:10+02:00",
      note: null,
    },
    {
      id: "val_002",
      field: "run_a.segment_1.length",
      label: "Süd — Abschnitt 1",
      valueMm: 5200,
      valueText: null,
      confidence: "high",
      confidenceLabel: "hoch",
      confidenceStep: 3,
      confidenceStepsTotal: 3,
      status: "auto",
      provenance: "vlm",
      source: {
        documentId: "doc_demo_0001",
        page: 1,
        bbox: { x: 0.28, y: 0.63, width: 0.14, height: 0.03 },
        originalText: "5,20 m",
      },
      confirmedBy: null,
      confirmedAt: null,
      note: null,
    },
    {
      id: "val_003",
      field: "run_a.segment_2.length",
      label: "Ost — Abschnitt 2",
      valueMm: 2400,
      valueText: null,
      confidence: "high",
      confidenceLabel: "hoch",
      confidenceStep: 3,
      confidenceStepsTotal: 3,
      status: "auto",
      provenance: "vlm",
      source: {
        documentId: "doc_demo_0001",
        page: 1,
        bbox: { x: 0.63, y: 0.44, width: 0.09, height: 0.03 },
        originalText: "2,40",
      },
      confirmedBy: null,
      confirmedAt: null,
      note: null,
    },
    {
      id: "val_004",
      field: "run_a.segment_3.length",
      label: "Nord — Rücksprung",
      valueMm: 2800,
      valueText: null,
      confidence: "medium",
      confidenceLabel: "mittel",
      confidenceStep: 2,
      confidenceStepsTotal: 3,
      status: "review",
      provenance: "vlm",
      source: {
        documentId: "doc_demo_0001",
        page: 1,
        bbox: { x: 0.52, y: 0.31, width: 0.1, height: 0.03 },
        originalText: "2,8?",
      },
      confirmedBy: null,
      confirmedAt: null,
      note: "Ziffer im Original überschrieben. Bestätigung nötig.",
    },
    {
      id: "val_005",
      field: "run_a.segment_1.interruption",
      label: "Unterbrechung — Terrassentür",
      valueMm: 1100,
      valueText: null,
      confidence: "high",
      confidenceLabel: "hoch",
      confidenceStep: 3,
      confidenceStepsTotal: 3,
      status: "auto",
      provenance: "vlm",
      source: {
        documentId: "doc_demo_0001",
        page: 1,
        bbox: { x: 0.35, y: 0.58, width: 0.08, height: 0.03 },
        originalText: "Tür 1,10",
      },
      confirmedBy: null,
      confirmedAt: null,
      note: null,
    },
    {
      id: "val_006",
      field: "run_b.segment_2.length",
      label: "Nord — Abschnitt 2",
      valueMm: 17500,
      valueText: null,
      confidence: "medium",
      confidenceLabel: "mittel",
      confidenceStep: 2,
      confidenceStepsTotal: 3,
      status: "conflict",
      provenance: "vlm",
      source: {
        documentId: "doc_demo_0001",
        page: 1,
        bbox: { x: 0.19, y: 0.22, width: 0.11, height: 0.03 },
        originalText: "17,5",
      },
      confirmedBy: null,
      confirmedAt: null,
      note: "Teilzug schließt nicht: Klaffung 16.000 mm. Wert prüfen.",
    },
    {
      id: "val_007",
      field: "run_b.segment_3.length",
      label: "Nord — Abschnitt 3",
      valueMm: 1500,
      valueText: null,
      confidence: "high",
      confidenceLabel: "hoch",
      confidenceStep: 3,
      confidenceStepsTotal: 3,
      status: "auto",
      provenance: "vlm",
      source: {
        documentId: "doc_demo_0001",
        page: 1,
        bbox: { x: 0.14, y: 0.18, width: 0.09, height: 0.03 },
        originalText: "1,50",
      },
      confirmedBy: null,
      confirmedAt: null,
      note: null,
    },
    {
      id: "val_008",
      field: "run_c.segment_1.length",
      label: "West — Teilstück",
      valueMm: 2000,
      valueText: null,
      confidence: "high",
      confidenceLabel: "hoch",
      confidenceStep: 3,
      confidenceStepsTotal: 3,
      status: "auto",
      provenance: "vlm",
      source: {
        documentId: "doc_demo_0001",
        page: 1,
        bbox: { x: 0.08, y: 0.49, width: 0.09, height: 0.03 },
        originalText: "2,00",
      },
      confirmedBy: null,
      confirmedAt: null,
      note: null,
    },
    {
      id: "val_009",
      field: "run_c.segment_2.length",
      label: "West — Fortsetzung",
      valueMm: null,
      valueText: null,
      confidence: "low",
      confidenceLabel: "niedrig",
      confidenceStep: 1,
      confidenceStepsTotal: 3,
      status: "neutral",
      provenance: "vlm",
      source: null,
      confirmedBy: null,
      confirmedAt: null,
      note: "In der Skizze nicht bemaßt. Bleibt offen (E-14).",
    },
    {
      id: "val_010",
      field: "case.material",
      label: "Material",
      valueMm: null,
      valueText: "Edelstahl 1.4301",
      confidence: "medium",
      confidenceLabel: "mittel",
      confidenceStep: 2,
      confidenceStepsTotal: 3,
      status: "review",
      provenance: "manual",
      source: {
        documentId: "doc_demo_0001",
        page: 1,
        bbox: { x: 0.09, y: 0.12, width: 0.26, height: 0.04 },
        originalText: "Edelstahl",
      },
      confirmedBy: null,
      confirmedAt: null,
      note: "Werkstoffnummer nicht im Dokument — manuell ergänzt, Bestätigung nötig.",
    },
  ],
};

export const SAMPLE_PLAN: CasePlan = {
  caseId: SAMPLE_CASE.id,
  planVersion: 3,
  runs: [
    {
      id: "run_a",
      label: "Teilzug A — Süd/Ost/Nord/West",
      nodes: [
        { id: "a1", xMm: 0, yMm: 0 },
        { id: "a2", xMm: 5200, yMm: 0 },
        { id: "a3", xMm: 5200, yMm: 2400 },
        { id: "a4", xMm: 8000, yMm: 2400 },
        { id: "a5", xMm: 8000, yMm: 6000 },
        { id: "a6", xMm: 0, yMm: 6000 },
      ],
      edges: [
        {
          id: "a_e1",
          fromNodeId: "a1",
          toNodeId: "a2",
          lengthMm: 5200,
          hasGrating: true,
          status: "auto",
          valueId: "val_002",
          interruption: "Terrassentür 1.100 mm",
          label: "Süd — Abschnitt 1",
        },
        {
          id: "a_e2",
          fromNodeId: "a2",
          toNodeId: "a3",
          lengthMm: 2400,
          hasGrating: true,
          status: "auto",
          valueId: "val_003",
          interruption: null,
          label: "Ost — Abschnitt 2",
        },
        {
          id: "a_e3",
          fromNodeId: "a3",
          toNodeId: "a4",
          lengthMm: 2800,
          hasGrating: true,
          status: "review",
          valueId: "val_004",
          interruption: null,
          label: "Nord — Rücksprung",
        },
        {
          id: "a_e4",
          fromNodeId: "a4",
          toNodeId: "a5",
          lengthMm: 3600,
          hasGrating: true,
          status: "confirmed",
          valueId: null,
          interruption: null,
          label: "Ost — Abschnitt 4",
        },
        {
          id: "a_e5",
          fromNodeId: "a5",
          toNodeId: "a6",
          lengthMm: 8000,
          hasGrating: false,
          status: "neutral",
          valueId: null,
          interruption: null,
          label: "Nord — ohne Rost",
        },
        {
          id: "a_e6",
          fromNodeId: "a6",
          toNodeId: "a1",
          lengthMm: 6000,
          hasGrating: false,
          status: "neutral",
          valueId: null,
          interruption: null,
          label: "West — ohne Rost",
        },
      ],
      closure: {
        sumHorizontalMm: 0,
        sumVerticalMm: 0,
        evaluable: true,
        status: "confirmed",
        message: "Teilzug schließt: Σh = 0 mm, Σv = 0 mm.",
      },
    },
    {
      id: "run_b",
      label: "Teilzug B — Nordhof",
      nodes: [
        { id: "b1", xMm: 0, yMm: 0 },
        { id: "b2", xMm: 3600, yMm: 0 },
        { id: "b3", xMm: 3600, yMm: 17500 },
        { id: "b4", xMm: 0, yMm: 17500 },
        { id: "b5", xMm: 0, yMm: 16000 },
      ],
      edges: [
        {
          id: "b_e1",
          fromNodeId: "b1",
          toNodeId: "b2",
          lengthMm: 3600,
          hasGrating: true,
          status: "auto",
          valueId: null,
          interruption: null,
          label: "Nord — Abschnitt 1",
        },
        {
          id: "b_e2",
          fromNodeId: "b2",
          toNodeId: "b3",
          lengthMm: 17500,
          hasGrating: true,
          status: "conflict",
          valueId: "val_006",
          interruption: null,
          label: "Nord — Abschnitt 2",
        },
        {
          id: "b_e3",
          fromNodeId: "b3",
          toNodeId: "b4",
          lengthMm: 3600,
          hasGrating: true,
          status: "auto",
          valueId: null,
          interruption: null,
          label: "Nord — Abschnitt 3",
        },
        {
          id: "b_e4",
          fromNodeId: "b4",
          toNodeId: "b5",
          lengthMm: 1500,
          hasGrating: true,
          status: "auto",
          valueId: "val_007",
          interruption: null,
          label: "Nord — Abschnitt 4",
        },
      ],
      closure: {
        sumHorizontalMm: 0,
        sumVerticalMm: 16000,
        evaluable: true,
        status: "conflict",
        message:
          "Teilzug schließt nicht: Σv = 16.000 mm. Ein Maß trägt den Fehler — 17,5 m statt 1,5 m ist die naheliegende Lesart.",
      },
    },
    {
      id: "run_c",
      label: "Teilzug C — West (Teilstück)",
      nodes: [
        { id: "c1", xMm: 0, yMm: 0 },
        { id: "c2", xMm: 2000, yMm: 0 },
        { id: "c3", xMm: 2000, yMm: 1800 },
      ],
      edges: [
        {
          id: "c_e1",
          fromNodeId: "c1",
          toNodeId: "c2",
          lengthMm: 2000,
          hasGrating: true,
          status: "auto",
          valueId: "val_008",
          interruption: null,
          label: "West — Teilstück",
        },
        {
          id: "c_e2",
          fromNodeId: "c2",
          toNodeId: "c3",
          lengthMm: null,
          hasGrating: false,
          status: "neutral",
          valueId: "val_009",
          interruption: null,
          label: "West — Fortsetzung",
        },
      ],
      closure: {
        sumHorizontalMm: 0,
        sumVerticalMm: 0,
        evaluable: false,
        status: "neutral",
        message: "Teilzug enthält unbemaßte Abschnitte — keine Schließungsprüfung (E-15).",
      },
    },
  ],
};
